import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Users, Shield, Loader2, Save, UserPlus, Trash2, CheckCircle, Lock
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UsersSecuritySection = ({ api }) => {
  const [team, setTeam] = useState([]);
  const [activeTab, setActiveTab] = useState('team');
  const [inviteDialog, setInviteDialog] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'support' });
  const [processing, setProcessing] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState({
    min_length: 8, require_uppercase: false, require_lowercase: false,
    require_numbers: false, require_special: false
  });
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securitySaved, setSecuritySaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setTeam(await api('/team'));
        try {
          const settings = await api('/settings');
          if (settings.password_policy) setPasswordPolicy(settings.password_policy);
        } catch {}
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  const handleInvite = async () => {
    if (!newMember.name || !newMember.email) return;
    setProcessing(true);
    try {
      await api('/team/invite', { method: 'POST', body: JSON.stringify(newMember) });
      setTeam(await api('/team'));
      setInviteDialog(false);
      setNewMember({ name: '', email: '', role: 'support' });
    } catch (err) { alert(err.message); }
    finally { setProcessing(false); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api(`/team/${userId}`, { method: 'DELETE' });
      setTeam(await api('/team'));
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="platform-users-security">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Users & Security</h2>
          <p className="text-sm text-slate-500 mt-1">Manage platform administrators and security settings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={activeTab === 'team' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${activeTab === 'team' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setActiveTab('team')}>
            <Users className="w-4 h-4 mr-2" /> Team Members
          </Button>
          <Button variant={activeTab === 'security' ? 'default' : 'outline'} size="sm"
            className={`rounded-xl ${activeTab === 'security' ? 'bg-slate-800 text-white' : ''}`}
            onClick={() => setActiveTab('security')}>
            <Lock className="w-4 h-4 mr-2" /> Security Settings
          </Button>
        </div>
      </div>

      {activeTab === 'team' && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setInviteDialog(true)} className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-sm" size="sm" data-testid="invite-team-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Member
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-2xl font-bold text-slate-800">{team.length}</p>
              <p className="text-xs text-slate-500">Total Members</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-2xl font-bold text-slate-800">{team.filter(m => m.platform_role === 'admin').length}</p>
              <p className="text-xs text-slate-500">Admins</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-2xl font-bold text-slate-800">{team.filter(m => m.platform_role === 'support').length}</p>
              <p className="text-xs text-slate-500">Support Staff</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-2xl font-bold text-slate-800">{team.filter(m => m.status === 'active' || !m.status).length}</p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase">Member</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase">Joined</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {team.map(m => (
                  <tr key={m.user_id} className="hover:bg-slate-50" data-testid={`team-member-${m.user_id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                          {m.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{m.name}</p>
                          <p className="text-xs text-slate-400">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                        m.platform_role === 'admin' ? 'bg-blue-100 text-blue-700' :
                        m.platform_role === 'support' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>{m.platform_role || 'admin'}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${m.status === 'disabled' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {m.status === 'disabled' ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}</td>
                    <td className="px-5 py-4 text-right">
                      {m.email !== 'superadmin@ihostpms.com' ? (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleRemoveMember(m.user_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : <span className="text-xs text-slate-400 italic">Protected</span>}
                    </td>
                  </tr>
                ))}
                {team.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <p className="font-medium">No team members yet</p>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Platform Roles</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Admin</span>
                <p className="text-sm text-slate-600 mt-2">Full platform access. Can manage all settings, tenants, and team members.</p>
              </div>
              <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Support</span>
                <p className="text-sm text-slate-600 mt-2">Can view tenants and assist users. Login-as functionality for support.</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">Viewer</span>
                <p className="text-sm text-slate-600 mt-2">Read-only access to platform data and analytics.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'security' && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
            <div className="flex items-center gap-2 mb-6">
              <Lock className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Password Policy</h3>
            </div>
            <p className="text-sm text-slate-500 mb-6">Define password requirements for all users on the platform.</p>
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium">Minimum Password Length</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input type="number" min="6" max="32" value={passwordPolicy.min_length}
                    onChange={(e) => setPasswordPolicy(p => ({ ...p, min_length: parseInt(e.target.value) || 8 }))}
                    className="w-24" data-testid="security-min-length" />
                  <span className="text-sm text-slate-500">characters (6-32)</span>
                </div>
              </div>
              <div className="space-y-3 pt-2">
                <Label className="text-sm font-medium">Password Requirements</Label>
                {[
                  { key: 'require_uppercase', label: 'Require uppercase letter (A-Z)', desc: 'At least one uppercase character' },
                  { key: 'require_lowercase', label: 'Require lowercase letter (a-z)', desc: 'At least one lowercase character' },
                  { key: 'require_numbers', label: 'Require number (0-9)', desc: 'At least one numeric character' },
                  { key: 'require_special', label: 'Require special character (!@#$%...)', desc: 'At least one special character' },
                ].map(req => (
                  <div key={req.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm text-slate-700">{req.label}</p>
                      <p className="text-xs text-slate-400">{req.desc}</p>
                    </div>
                    <Switch checked={passwordPolicy[req.key]}
                      onCheckedChange={(checked) => setPasswordPolicy(p => ({ ...p, [req.key]: checked }))}
                      data-testid={`security-${req.key.replace('_', '-')}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-3">
              <Button onClick={async () => {
                setSecuritySaving(true);
                try {
                  await api('/settings', { method: 'PUT', body: JSON.stringify({ password_policy: passwordPolicy }) });
                  setSecuritySaved(true);
                  setTimeout(() => setSecuritySaved(false), 3000);
                } catch (err) { console.error(err); }
                finally { setSecuritySaving(false); }
              }} disabled={securitySaving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="save-security-btn">
                {securitySaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Password Policy
              </Button>
              {securitySaved && <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Saved</span>}
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mt-4">
            <p className="text-sm font-medium text-slate-600 mb-2">Current Policy Preview</p>
            <ul className="text-sm text-slate-500 space-y-1">
              <li>- Minimum {passwordPolicy.min_length} characters</li>
              {passwordPolicy.require_uppercase && <li>- At least one uppercase letter</li>}
              {passwordPolicy.require_lowercase && <li>- At least one lowercase letter</li>}
              {passwordPolicy.require_numbers && <li>- At least one number</li>}
              {passwordPolicy.require_special && <li>- At least one special character</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription className="sr-only">Invite a new team member to the platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={newMember.name} onChange={e => setNewMember(m => ({ ...m, name: e.target.value }))} placeholder="Full name" className="mt-1" /></div>
            <div><Label>Email</Label><Input type="email" value={newMember.email} onChange={e => setNewMember(m => ({ ...m, email: e.target.value }))} placeholder="email@example.com" className="mt-1" /></div>
            <div>
              <Label>Role</Label>
              <select value={newMember.role} onChange={e => setNewMember(m => ({ ...m, role: e.target.value }))} className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm">
                <option value="admin">Admin</option>
                <option value="support">Support</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <Button onClick={handleInvite} disabled={processing} className="w-full bg-slate-800 hover:bg-slate-900 text-white">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Send Invite
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
